<template>
  <v-card
    class="py-0 ma-0"
  >
    <v-toolbar flat>
      <v-toolbar-title>Claim Entitlements</v-toolbar-title>
    </v-toolbar>

    <v-data-table
      hide-default-footer
      :headers="headers"
      :items="claimEntitlements"
      :single-expand="true"
      :items-per-page="-1"
      show-expand
    >
      <template v-slot:item.name="{ item }">
        <div>
          <span class="mr-4">{{ item.name }}</span>
        </div>
      </template>
      <template v-slot:item.claimed_by="{ item }">
        <span>{{ item.claimed_by }}</span>
      </template>

      <template 
        v-slot:expanded-item="{ headers, item }"
      >
        <td
          :colspan="headers.length"
          class="pb-2"
          style="background-color: rgba(0, 0, 0, .05);"
        >
          <div
            style="max-height: 300px; overflow-y: auto;"
          >
            <v-simple-table
              class="px-4 pb-3"
            >
              <!-- got claim type details but no claim entitlement settings -->
              <template
                v-if="item.details && item.details.length > 0 && !item.claim_entitlement_settings"
                v-slot:default
              >
                <tbody>
                  <tr
                    v-for="(detail, i) in item.details"
                    :key="i"
                  >
                    <td>
                      {{ detail.name }} (RM {{ thousandSeparator((detail.amount / 100).toFixed(2)) }} /{{ detail.unit_of_measurement }})
                    </td>
                  </tr>
                </tbody>
              </template>

              <!-- got claim type details & claim entitlement settings -->
              <template
                v-else-if="(item.details && item.details.length > 0) && item.claim_entitlement_settings"
                v-slot:default
              >
                <tbody>
                  <tr
                    v-for="(settingDetail, i) in item.claim_entitlement_settings.claim_entitlement_setting_details"
                    :key="i"
                  >
                    <td>
                      Maximum RM {{ thousandSeparator((settingDetail.max_amount / 100).toFixed(2)) }} /{{ item.claim_entitlement_settings.credit_unit }}
                      <br>
                      <span
                        v-for="(detail, j) in item.details"
                        :key="'detail-' + j"
                      >
                        &nbsp;&nbsp; • {{ detail.name }} (RM {{ thousandSeparator((detail.amount / 100).toFixed(2)) }} /{{ detail.unit_of_measurement }})<br>
                      </span>
                    </td>
                  </tr>
                </tbody>
              </template>

              <!-- got claim entitlement settings & claim entitlements but no claim type details -->
              <template
                v-else-if="!(item.details && item.details.length > 0) && item.claim_entitlement_settings && item.claim_entitlement_settings.claim_entitlements.length > 0"
                v-slot:default
              >
                <!-- Header -->
                <!-- daily / monthly -->
                <thead v-if="item.claim_entitlement_settings.credit_unit !== 'year'">
                  <tr>
                    <th class="text-left">
                      <span>Entitlement</span>
                    </th>
                    <th class="text-left">
                      <span>Available</span>
                    </th>
                  </tr>
                </thead>
                <!-- yearly -->
                <thead v-else>
                  <tr>
                    <th class="text-left">
                      <span>Entitlement</span>
                    </th>
                    <th
                      class="text-left"
                      style="width: 110px"
                    >
                      <span>Start Date</span>
                    </th>
                    <th
                      class="text-left"
                      style="width: 110px"
                    >
                      <span>End Date</span>
                    </th>
                    <th class="text-right">
                      <span>Current Available (RM)</span>
                    </th>
                    <th class="text-right">
                      <span>Adjustment (RM)</span>
                    </th>
                    <th class="text-right">
                      <span>Pending Claim (RM)</span>
                    </th>
                    <th class="text-right">
                      <span>Claimed Amount (RM)</span>
                    </th>
                    <th class="text-right">
                      <span>Balance (RM)</span>
                    </th>
                  </tr>
                </thead>
                <!-- Body -->
                <!-- daily / monthly -->
                <tbody v-if="item.claim_entitlement_settings.credit_unit !== 'year'">
                  <tr
                    v-for="(entitlement, i) in item.claim_entitlement_settings.claim_entitlement_setting_details"
                    :key="i"
                  >
                    <td>
                      {{ item.claim_entitlement_settings.name }}
                    </td>
                    <td>
                      RM {{ thousandSeparator((entitlement.max_amount / 100).toFixed(2)) }} /{{ item.claim_entitlement_settings.credit_unit }}
                    </td>
                  </tr>
                </tbody>
                <!-- yearly -->
                <tbody v-else>
                  <tr
                    v-for="(entitlement, i) in item.claim_entitlement_settings.claim_entitlements"
                    :key="i"
                  >
                    <td>
                      {{ item.claim_entitlement_settings.name }}
                    </td>
                    <td>
                      {{ entitlement.start_date }}
                    </td>
                    <td>
                      {{ entitlement.end_date }}
                    </td>
                    <td class="text-right">
                      {{ thousandSeparator((entitlement.current_available / 100).toFixed(2)) }}
                    </td>
                    <td class="text-right">
                      {{ thousandSeparator((entitlement.adjusted_amount / 100).toFixed(2)) }}
                    </td>
                    <td class="text-right">
                      {{ thousandSeparator((entitlement.pending_amt / 100).toFixed(2)) }}
                    </td>
                    <td class="text-right">
                      {{ thousandSeparator((entitlement.claimed_amount / 100).toFixed(2)) }}
                    </td>
                    <td class="text-right">
                      {{ thousandSeparator(((entitlement.current_available - entitlement.pending_amt - entitlement.claimed_amount + entitlement.adjusted_amount) / 100).toFixed(2)) }}
                    </td>
                  </tr>
                </tbody>
              </template>
              
              <!-- else will be no limit claim -->
              <template
                v-else
                v-slot:default
              >
                <thead>
                  <tr>
                    <td class="grey--text">
                      No limit
                    </td>
                  </tr>
                </thead>
              </template>
            </v-simple-table>
          </div>
        </td>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
  export default {
    props: {
      claimEntitlements: {
        type: Array,
        required: true,
      },
    },
    data () {
      return {
        headers: [
          {
            text: 'Claim Type',
            align: 'left',
            sortable: false,
            value: 'name',
          },
          {
            text: 'Claimed by',
            align: 'left',
            sortable: false,
            value: 'claimed_by',
          },
        ],
      }
    },
    methods: {
      thousandSeparator (amount) {
        if (amount !== '' || amount !== undefined || amount !== 0 || amount !== '0' || amount !== null) {
          return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        } else {
          return amount
        }
      },
    },
  }
</script>
